import { roundMoney, sumMoney, toMoney } from '@finvox/shared/money';
import { payVoxDailyTotalMemo } from '@finvox/shared/cashbook';

/** PayVox sale status in the FinVox mirror collection. */
export const PAYVOX_SALE_STATUSES = ['active', 'refunded', 'voided'] as const;
export type PayVoxSaleStatus = (typeof PAYVOX_SALE_STATUSES)[number];

/**
 * PayVox publishes closed sales to FinVox Firestore at this path.
 * Same Firebase project; orgId must match FinVox organization id.
 */
export function payVoxSalesMirrorPath(orgId: string): string {
  return `organizations/${orgId}/payvoxSales`;
}

/** Alternate path some PayVox builds use for daily net totals. */
export function payVoxDailySalesMirrorPath(orgId: string): string {
  return `organizations/${orgId}/payvoxDailySales`;
}

export function payVoxSalesMirrorPaths(orgId: string): string[] {
  return [payVoxSalesMirrorPath(orgId), payVoxDailySalesMirrorPath(orgId)];
}

/** Document shape PayVox writes when a sale closes (or is refunded). */
export type PayVoxSaleDoc = {
  id: string;
  /** Business sale date YYYY-MM-DD */
  date: string;
  /** Net amount after discounts/refunds; positive for active sales */
  amount: string | number;
  receiptId?: string;
  paymentMethod?: string;
  status?: PayVoxSaleStatus;
  closedAt?: string;
  updatedAt?: string;
};

export function payVoxDayKey(date: string): string {
  return `payvox:day:${date}`;
}

const DAILY_DOC_ID = /^daily-(\d{4}-\d{2}-\d{2})$/i;

function toIsoDateString(value: unknown): string | null {
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1]! : null;
  }
  if (value && typeof value === 'object') {
    if ('toDate' in value && typeof (value as { toDate: () => Date }).toDate === 'function') {
      return (value as { toDate: () => Date }).toDate().toISOString().slice(0, 10);
    }
    if ('_seconds' in value && typeof (value as { _seconds: number })._seconds === 'number') {
      return new Date((value as { _seconds: number })._seconds * 1000).toISOString().slice(0, 10);
    }
  }
  return null;
}

function readAmount(data: Record<string, unknown>): string | number | undefined {
  for (const key of ['amount', 'netAmount', 'netSales', 'net', 'total']) {
    const v = data[key];
    if (v !== undefined && v !== null && v !== '') return v as string | number;
  }
  return undefined;
}

function readDate(data: Record<string, unknown>, docId: string): string | null {
  for (const key of ['date', 'saleDate', 'businessDate', 'day']) {
    const parsed = toIsoDateString(data[key]);
    if (parsed) return parsed;
  }
  const fromClosed = toIsoDateString(data.closedAt);
  if (fromClosed) return fromClosed;
  const daily = DAILY_DOC_ID.exec(docId);
  if (daily) return daily[1]!;
  return null;
}

function normalizeStatus(raw: unknown): PayVoxSaleStatus | undefined {
  if (raw == null || raw === '') return 'active';
  const s = String(raw).toLowerCase();
  if (s === 'active' || s === 'refunded' || s === 'voided') return s;
  if (s === 'closed' || s === 'completed' || s === 'paid' || s === 'published') return 'active';
  return undefined;
}

/** Map raw Firestore payvoxSales doc → normalized sale (tolerates PayVox field variants). */
export function normalizePayVoxSaleFromFirestore(
  docId: string,
  data: Record<string, unknown>
): PayVoxSaleDoc | null {
  const date = readDate(data, docId);
  if (!date) return null;
  const amount = readAmount(data);
  if (amount === undefined) return null;
  const status = normalizeStatus(data.status);
  if (status === undefined) return null;

  return {
    id: docId,
    date,
    amount,
    status,
    receiptId: data.receiptId != null ? String(data.receiptId) : undefined,
    paymentMethod: data.paymentMethod != null ? String(data.paymentMethod) : undefined,
    closedAt: data.closedAt != null ? String(data.closedAt) : undefined,
    updatedAt: data.updatedAt != null ? String(data.updatedAt) : undefined
  };
}

export function isPayVoxSaleImportable(sale: PayVoxSaleDoc): boolean {
  const status = (sale.status ?? 'active').toLowerCase();
  return status === 'active';
}

export function payVoxSaleAmount(sale: PayVoxSaleDoc): string {
  return roundMoney(sale.amount ?? 0);
}

export function filterPayVoxSalesByDateRange(
  sales: PayVoxSaleDoc[],
  startDate: string,
  endDate: string
): PayVoxSaleDoc[] {
  return sales.filter((s) => s.date >= startDate && s.date <= endDate);
}

export type PayVoxCashbookImportMode = 'daily_total' | 'per_receipt';

export type PayVoxCashbookRow = {
  date: string;
  amount: string;
  memo: string;
  payvoxSaleId?: string;
  payvoxDayKey?: string;
};

export function buildPayVoxCashbookRows(
  sales: PayVoxSaleDoc[],
  mode: PayVoxCashbookImportMode,
  importedSaleIds: Set<string>,
  importedDayKeys: Set<string>,
  locale?: string
): { rows: PayVoxCashbookRow[]; skippedCount: number } {
  const active = sales.filter(isPayVoxSaleImportable);
  let skippedCount = 0;

  if (mode === 'per_receipt') {
    const rows: PayVoxCashbookRow[] = [];
    for (const sale of active) {
      const amount = payVoxSaleAmount(sale);
      if (toMoney(amount).lte(0)) continue;
      if (importedSaleIds.has(sale.id)) {
        skippedCount += 1;
        continue;
      }
      const receipt = sale.receiptId?.trim() || sale.id;
      rows.push({
        date: sale.date,
        amount,
        memo: `PayVox #${receipt}`,
        payvoxSaleId: sale.id
      });
    }
    return { rows, skippedCount };
  }

  const byDay = new Map<string, string[]>();
  for (const sale of active) {
    const amount = payVoxSaleAmount(sale);
    if (toMoney(amount).lte(0)) continue;
    const list = byDay.get(sale.date) ?? [];
    list.push(amount);
    byDay.set(sale.date, list);
  }

  const rows: PayVoxCashbookRow[] = [];
  for (const [date, amounts] of [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const dayKey = payVoxDayKey(date);
    if (importedDayKeys.has(dayKey)) {
      skippedCount += amounts.length;
      continue;
    }
    rows.push({
      date,
      amount: sumMoney(amounts),
      memo: payVoxDailyTotalMemo(locale),
      payvoxDayKey: dayKey
    });
  }
  return { rows, skippedCount };
}
